import { memo, useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";

import { EditGuideTooltip } from "@/components/features/items/edit/_shared/ui/components/system/tooltip/edit-guide-tooltip";

import { Button } from "@/components/ui/button/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog/dialog";
import { InputTextField } from "@/components/ui/form/fields/input-field/input-text";
import { Label } from "@/components/ui/label/label";

type Props = {
	statusReceiptIssued: boolean;
	canIssueReceipt: boolean;
	/** 予約コンポーネントと同様、宛名入力の初期値（ゲスト名） */
	initialReceiptAddressee: string;
	//
	onUpdate: (statusReceiptIssued: boolean) => void;
	onIssueReceipt: (address?: string) => Promise<{ pdfUrl: string } | undefined>;
};

export const IssuedReceiptStatusOfManagementStatusCompositions = memo(
	({
		statusReceiptIssued,
		canIssueReceipt,
		initialReceiptAddressee,
		onUpdate,
		onIssueReceipt,
	}: Props) => {
		const isIssuedReceipt = statusReceiptIssued;

		const handleClickUpdateIssuedReceiptStatus = useCallback(() => {
			const confirmText = isIssuedReceipt
				? "領収書のステータス「発行済み」を取り消しますか？"
				: "領収書のステータスを「発行済み」に変更しますか？";
			if (window.confirm(confirmText)) {
				onUpdate(!isIssuedReceipt);
			}
		}, [ isIssuedReceipt, onUpdate ]);

		/**
		 * 領収書発行（予約詳細 driver のフローに揃える）
		 * - 「領収書を発行する」→ モーダルで宛名・発行 → 成功時はモーダルを閉じて PDF を別タブで開く
		 */
		const [ isReceiptDialogOpen, setIsReceiptDialogOpen ] = useState(false);
		const [ address, setAddress ] = useState("");
		const [ isIssuing, setIsIssuing ] = useState(false);

		const defaultAddressee = useMemo(
			() => (initialReceiptAddressee.trim() ? initialReceiptAddressee.trim() : ""),
			[ initialReceiptAddressee ],
		);

		const prevReceiptDialogOpen = useRef(false);
		useLayoutEffect(() => {
			if (isReceiptDialogOpen && !prevReceiptDialogOpen.current) {
				setAddress(defaultAddressee);
			}
			prevReceiptDialogOpen.current = isReceiptDialogOpen;
		}, [ isReceiptDialogOpen, defaultAddressee ]);

		const handleOpenReceiptDialog = useCallback(() => {
			setIsReceiptDialogOpen(true);
		}, []);

		const handleReceiptDialogOpenChange = useCallback((open: boolean) => {
			setIsReceiptDialogOpen(open);
			if (!open) {
				setAddress("");
				setIsIssuing(false);
			}
		}, []);

		const handleIssueReceipt = useCallback(async () => {
			setIsIssuing(true);
			try {
				const result = await onIssueReceipt(address.trim() ? address : undefined);
				if (result?.pdfUrl) {
					setIsReceiptDialogOpen(false);
					setAddress("");
					window.open(result.pdfUrl, "_blank", "noopener,noreferrer");
				}
			} finally {
				setIsIssuing(false);
			}
		}, [ address, onIssueReceipt ]);

		return (
			<div className="w-full space-y-4">
				{/* 既存のステータストグル */}
				<div className="space-y-2">
					<div className="flex items-center gap-2">
						<p>領収書のステータス</p>

						<EditGuideTooltip
							guide="
            お客様の予約詳細画面での領収書発行を停止させることができます。
            <br /><br />
            <b>「発行済み」</b>
            <br />
            このステータスに手動で設定すると、お客様の予約詳細画面からの領収書発行を停止させることができます。ホスト様のみが領収書を発行する場合は、こちらに設定してください。
            <br />
			※すでに領収書が発行されていた場合、「発行済み」の表示となります。
            <br /><br />
            <b>「未選択（初期設定）」</b>
            <br />
            ホスト様、お客様ともに領収書を発行できる状態です。※ホスト様が領収書を発行した場合でも、お客様は予約詳細画面から領収書を発行できます。
          "
						/>
					</div>

					<div className="flex items-center gap-x-7">
						<Label text="発行済み" variant={isIssuedReceipt ? "required" : "black"} />

						<Button variant="outline" type="button" size="sm" onClick={handleClickUpdateIssuedReceiptStatus}>
							変更
						</Button>
					</div>
				</div>

				{/* 領収書の発行 */}
				{canIssueReceipt && (
					<div className="space-y-2">
						<div className="flex items-center gap-2">
							<p>領収書の発行</p>

							<EditGuideTooltip
								guide="
            領収書は、ゲストご自身で予約通知メール内の予約詳細ページより発行可能です。
			<br />
            ゲストもしくはホストが一度発行すると、領収書には「再発行」の文言が入ります。
          "
							/>
						</div>

						{!isReceiptDialogOpen && (
							<Button variant="outline" type="button" size="sm" onClick={handleOpenReceiptDialog}>
								領収書を発行する
							</Button>
						)}

						<Dialog open={isReceiptDialogOpen} onOpenChange={handleReceiptDialogOpenChange}>
							<DialogContent className="max-w-lg px-6 py-8" aria-describedby={undefined}>
								<DialogHeader className="sr-only">
									<DialogTitle>領収書の発行</DialogTitle>
								</DialogHeader>

								<div className="space-y-9">
									<div className="space-y-2">
										<InputTextField
											label="領収書の宛名"
											value={address}
											handleChange={setAddress}
											placeholder={defaultAddressee ? undefined : "山田 太郎"}
											name="receipt-address"
											autoComplete="off"
										/>
										<p className="text-sm font-normal text-zinc-900 opacity-90">
											宛名が空欄の場合は、予約者の名前が入ります。
										</p>
									</div>

									<div className="flex items-center justify-end gap-x-3 border-t border-t-zinc-200 px-6 pt-4">
										<Button
											variant="outline"
											type="button"
											size="sm"
											onClick={() => handleReceiptDialogOpenChange(false)}
											disabled={isIssuing}
										>
											戻る
										</Button>
										<Button type="button" size="sm" onClick={handleIssueReceipt} disabled={isIssuing}>
											{isIssuing ? "発行中..." : "発行する"}
										</Button>
									</div>
								</div>
							</DialogContent>
						</Dialog>
					</div>
				)}
			</div>
		);
	},
);
